import {
  triggerToast,
  ButtonDS,
  DownloadOutlined,
  LaunchPage,
  usePersistMuiTableState,
  MaterialUiTable,
  MaterialUiColumnsProps,
  TextCapitalized,
  fromToDateFormatterDayMonthLongYear,
} from "@qivia/ui";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../../../../redux/hooks";
import {
  billingStatementFileExportAsync,
  billingStatementFilesListAsync,
  documentDownloadAsync,
  selectBillingStatementFileExportLink,
  selectBillingStatementFileExportStatus,
  selectBillingStatementFilesList,
  selectBillingStatementFilesListStatus,
} from "../billingsSlice";
import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import {
  BillingStatementFilesList,
  BillingStatementFilesListDisplayed,
} from "../billingsAPI";
import { selectCompany } from "../../homeSlice";
import "../../../../i18n/i18n";
import { useGridApiRef } from "@mui/x-data-grid-premium";
import { documentDownloadXlsxAsync } from "../../../../libSlice/downloadFileXslx";
import styled from "styled-components";
import { getDate } from "../../../../libSlice/materialUiFormats";

type BillingStatementFileRow = BillingStatementFilesList & { id: string };

export const BillingStatementsTab = () => {
  const { t } = useTranslation();

  const dispatch = useAppDispatch();
  const params = useParams();
  const company = useAppSelector(selectCompany);

  const billingStatementFilesListInit = useAppSelector(
    selectBillingStatementFilesList,
  );
  const billingStatementFilesListStatus = useAppSelector(
    selectBillingStatementFilesListStatus,
  );
  const billingStatementFileExportStatus = useAppSelector(
    selectBillingStatementFileExportStatus,
  );
  const billingStatementFileExportLink = useAppSelector(
    selectBillingStatementFileExportLink,
  );
  const [listDisplayed, setListDisplayed] = useState<
    BillingStatementFileRow[] | null
  >(null);

  const pageName = "billingStatements";
  const apiRef = useGridApiRef();
  const key = "customers-grid";

  const aggregation = {
    model: {
      fromDate: "size",
      bucketFileName: "size",
    },
  };

  const dataGridState = usePersistMuiTableState(
    apiRef,
    key,
    pageName,
    aggregation,
  );

  const billingStatementFilesList = useMemo(() => {
    return billingStatementFilesListInit.map((statementFile) => ({
      ...statementFile,
      id: statementFile.uuid,
      fromDate: fromToDateFormatterDayMonthLongYear(
        new Date(statementFile.fromDate),
        new Date(statementFile.toDate),
      ),
    }));
  }, [billingStatementFilesListInit]);

  useEffect(() => {
    if (
      billingStatementFilesList &&
      billingStatementFilesListStatus === "success"
    ) {
      setListDisplayed(billingStatementFilesList);
    }
  }, [billingStatementFilesList, billingStatementFilesListStatus]);

  useEffect(() => {
    if (company && billingStatementFilesListStatus === "idle") {
      void dispatch(billingStatementFilesListAsync(company.uuid));
    }
  }, [dispatch, company, billingStatementFilesListStatus]);

  useEffect(() => {
    if (billingStatementFileExportStatus === "success") {
      triggerToast(t("billings.statements.upload.success") || "", "valid");
    } else if (billingStatementFileExportStatus === "failed") {
      triggerToast(t("billings.statements.upload.failure") || "", "error");
    }
  }, [billingStatementFileExportStatus, t]);

  useEffect(() => {
    if (
      billingStatementFileExportLink.presignedUrl &&
      billingStatementFileExportLink.fileName
    ) {
      void dispatch(documentDownloadAsync(billingStatementFileExportLink));
    }
  }, [billingStatementFileExportLink, dispatch]);

  const columnsDisplayed: {
    [index in keyof BillingStatementFilesListDisplayed]: MaterialUiColumnsProps;
  } = useMemo(() => {
    return {
      fromDate: {
        type: "date",
        width: 300,
        valueGetter: getDate,
        valueFormatter: (_value, row) => {
          const rowData = row as BillingStatementFileRow;
          return rowData.fromDate ? (
            <TextCapitalized>{rowData.fromDate}</TextCapitalized>
          ) : (
            ""
          );
        },
      },
      bucketFileName: {
        type: "string",
        width: 200,
        valueFormatter: (value: string | null, row) => {
          return (
            value && (
              <StyledColumnButton>
                <ButtonDS
                  sizeButton={"S"}
                  format={"hug"}
                  buttonType={"secondary"}
                  singleIcon={{ icon: <DownloadOutlined />, size: "S" }}
                  onClick={() => {
                    const rowData = row as BillingStatementFilesList;
                    void dispatch(
                      billingStatementFileExportAsync({
                        bucketFileName: value,
                        fileName:
                          rowData.fileName ||
                          "QIVIA_" + t("billingStatements.title") + ".xlsx",
                      }),
                    );
                  }}
                />
              </StyledColumnButton>
            )
          );
        },
      },
    };
  }, [dispatch, t]);

  const downloadViewAction = (file: Buffer | undefined) => {
    if (!file) return;
    void dispatch(
      documentDownloadXlsxAsync({
        buffer: file,
        fileName: "QIVIA_" + t("billingStatements.title") + ".xlsx",
      }),
    );
  };

  if (params.tab !== "statements") {
    return;
  }

  const searchBarProps = {
    values: billingStatementFilesList,
    setFilterValues: (filtered: BillingStatementFilesListDisplayed[]) =>
      setListDisplayed(filtered as BillingStatementFileRow[]),
    keysToIgnore: [
      "bucketFileName" as const,
      "uuid" as const,
      "toDate" as const,
    ],
  };

  if (!listDisplayed) return <LaunchPage hasBorderRadius={true} />;

  return (
    <MaterialUiTable<
      keyof BillingStatementFilesListDisplayed,
      BillingStatementFilesListDisplayed
    >
      rows={listDisplayed}
      columns={columnsDisplayed}
      pageName={pageName}
      dataGridState={dataGridState}
      searchBar={searchBarProps}
      apiRef={apiRef}
      downloadViewAction={downloadViewAction}
    />
  );
};

const StyledColumnButton = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  height: 100%;
`;
